import { useState, type FormEvent } from 'react';
import {
  ArrowRight,
  AtSign,
  ChevronLeft,
  Clock,
  Handshake,
  Loader2,
  Phone,
  ShieldCheck,
  User,
} from 'lucide-react';
import { TrustBar } from '../../conversion/TrustBar';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { PhoneInput, type PhoneInputValue } from './PhoneInput';
import { useComparateurStore } from './useComparateurStore';
import { StepEyebrow } from './StepEyebrow';
import { dossierContactInputSchema, type DossierContactInput } from '@shared/schemas/comparateur';
import { DEFAULT_PHONE_COUNTRY, findPhoneCountryByDial } from '@shared/constants/phone-codes';

export function StepContact() {
  const store = useComparateurStore();
  const existing = store.dossier?.contact ?? null;

  const [firstName, setFirstName] = useState(existing?.firstName ?? '');
  const [lastName, setLastName] = useState(existing?.lastName ?? '');
  const [email, setEmail] = useState(existing?.email ?? '');
  // Seul l'indicatif (dial) est stocké côté serveur : on résout l'ISO2 à la reprise
  // pour réafficher le bon pays (ex. dial 32 → BE, pas FR).
  const existingCountry = existing?.phoneCountryCode
    ? findPhoneCountryByDial(existing.phoneCountryCode)
    : undefined;
  const [phone, setPhone] = useState<PhoneInputValue>({
    countryIso2: existingCountry?.iso2 ?? DEFAULT_PHONE_COUNTRY.iso2,
    countryDial: existingCountry?.dial ?? existing?.phoneCountryCode ?? DEFAULT_PHONE_COUNTRY.dial,
    national: existing?.phoneNational ?? '',
  });

  const [issues, setIssues] = useState<Partial<Record<keyof DossierContactInput, string>>>({});

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const candidate = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phoneCountryCode: phone.countryDial,
      phoneNational: phone.national.replace(/\D/g, ''),
    };

    const result = dossierContactInputSchema.safeParse(candidate);
    if (!result.success) {
      const errs: Partial<Record<keyof DossierContactInput, string>> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof DossierContactInput | undefined;
        if (key) errs[key] = issue.message;
      }
      setIssues(errs);
      return;
    }
    setIssues({});

    try {
      await store.saveContact(result.data);
    } catch {
      /* error already set in store */
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => store.goBack()}
        className="-ml-1 mb-5 inline-flex min-h-11 items-center gap-1 rounded-md px-2 py-2 text-[12px] text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-700"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        Retour
      </button>

      <StepEyebrow step="contact" />
      <h3 className="h-section mt-2">Qui recevra votre comparatif énergétique ?</h3>
      <p className="mt-1.5 text-[13px] leading-relaxed text-stone-500">
        Un consultant énergie vous recontacte sous 24 h ouvrées avec un comparatif chiffré (€/an,
        conditions d'engagement) — sans engagement de changement de votre côté.
      </p>

      {store.dossier?.company && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-brand-100 bg-brand-50/60 px-3.5 py-2.5">
          <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-brand-600" />
          <span className="truncate text-[12px] text-brand-900">
            Dossier de <strong>{store.dossier.company.name}</strong>
            {store.dossier.company.nafCode && (
              <span className="text-brand-700"> · NAF {store.dossier.company.nafCode}</span>
            )}
          </span>
        </div>
      )}

      {store.error && (
        <Alert variant="error" className="mt-4">
          <AlertDescription className="text-[12px]">{store.error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="contact-firstname" className="text-[13px] font-semibold">
              Prénom
            </Label>
            <div className="relative mt-1.5">
              <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
              <Input
                id="contact-firstname"
                autoComplete="given-name"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Jean"
                className="h-12 rounded-xl border-2 border-stone-200 pl-9 text-[15px] focus:border-brand-400"
                aria-invalid={!!issues.firstName}
              />
            </div>
            {issues.firstName && <FieldError>{issues.firstName}</FieldError>}
          </div>

          <div>
            <Label htmlFor="contact-lastname" className="text-[13px] font-semibold">
              Nom
            </Label>
            <Input
              id="contact-lastname"
              autoComplete="family-name"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Dupont"
              className="mt-1.5 h-12 rounded-xl border-2 border-stone-200 text-[15px] focus:border-brand-400"
              aria-invalid={!!issues.lastName}
            />
            {issues.lastName && <FieldError>{issues.lastName}</FieldError>}
          </div>
        </div>

        <div>
          <Label htmlFor="contact-email" className="text-[13px] font-semibold">
            E-mail professionnel
          </Label>
          <div className="relative mt-1.5">
            <AtSign className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <Input
              id="contact-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@entreprise.fr"
              className="h-12 rounded-xl border-2 border-stone-200 pl-9 text-[15px] focus:border-brand-400"
              aria-invalid={!!issues.email}
            />
          </div>
          {issues.email && <FieldError>{issues.email}</FieldError>}
          <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-stone-400">
            <ShieldCheck className="h-3 w-3 shrink-0 text-brand-500" />
            Nous l&apos;utilisons uniquement pour vous transmettre votre comparatif et reprendre
            votre dossier en cas d&apos;interruption.
          </p>
        </div>

        <div>
          <Label htmlFor="contact-phone" className="text-[13px] font-semibold">
            Téléphone <span className="text-stone-400">(avec indicatif)</span>
          </Label>
          <div className="relative mt-1.5">
            <PhoneInput
              id="contact-phone"
              value={phone}
              onChange={setPhone}
              error={issues.phoneNational ?? issues.phoneCountryCode}
            />
          </div>
          <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-stone-400">
            <Phone className="h-3 w-3 shrink-0" />
            Optionnel — accélère la prise de contact si renseigné.
          </p>
        </div>

        <Button
          type="submit"
          variant="brand"
          size="xl"
          disabled={store.loading}
          className="w-full gap-2"
        >
          {store.loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Enregistrement…
            </>
          ) : (
            <>
              Voir mon comparatif
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>

        {/* Réassurance au moment de la conversion (parité avec les autres funnels) */}
        <div className="border-t border-stone-100 pt-4">
          <TrustBar
            items={[
              { icon: ShieldCheck, label: "Courtier indépendant des fournisseurs d'énergie" },
              { icon: Handshake, label: 'Sans engagement' },
              { icon: Clock, label: 'Réponse sous 24 h ouvrées' },
            ]}
          />
        </div>
      </form>
    </div>
  );
}

function FieldError({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-1 text-[12px] font-medium text-red-600" role="alert">
      {children}
    </p>
  );
}
